/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  DataTable,
  Table,
  TableHead,
  TableExpandHeader,
  TableContainer,
  TableRow,
  TableHeader,
  TableBody,
  TableToolbarContent
} from 'carbon-components-react';
import cn from 'classnames';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { getInvalidResponseStr } from '../../helpers';
import { CatalogOpenApiMessages } from '../../messages';
import { InvalidResponseMethodWarning } from '../InvalidResponseMethodWarning';
import { MappingStepMessages as messages } from '../messages';
import { ENDPOINT_HEADERS } from './constants';
import EndpointRow from './EndpointRow';

interface EndpointsProps {
  /**
   *
   */
  invalidResponses: any;
  /**
   *
   */
  endpoints: any;
}
export const Endpoints = ({ invalidResponses, endpoints }: EndpointsProps) => {
  const parametersAndResponseVarsAllValid =
    invalidResponses && invalidResponses?.length === 0;

  // When endpoints dp not have ids or ids are undefined, this will add ids to the endpoints
  // This is required for carbon DataTable, otherwise it will give console warning
  const endpointsWithIds = endpoints.map(endpoint => {
    if (!endpoint?.id) {
      endpoint.id = uuidv4();
    }
    return endpoint;
  });

  return (
    <DataTable
      rows={endpointsWithIds}
      headers={ENDPOINT_HEADERS}
      render={({ headers, getHeaderProps, getTableProps }) => {
        return (
          <TableContainer
            title={
              <FormattedMessage {...CatalogOpenApiMessages.reviewOperations} />
            }
            description={
              <FormattedMessage {...messages.tableDescriptionReadOnly} />
            }>
            {!parametersAndResponseVarsAllValid && (
              <TableToolbarContent>
                <InvalidResponseMethodWarning
                  invalidResponsesStr={getInvalidResponseStr(invalidResponses)}
                />
              </TableToolbarContent>
            )}
            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  <TableExpandHeader />
                  {headers.map((header, index) => (
                    <TableHeader
                      className={cn({
                        CatalogOpenApiDataTable__method:
                          header.header === 'Method',
                        CatalogOpenApiDataTable__operation:
                          header.header === 'Operation',
                        CatalogOpenApiDataTable__resource:
                          header.header === 'Resource',
                        CatalogOpenApiDataTable__row: true
                      })}
                      key={`${header.key}_${index}`}
                      {...getHeaderProps({ header })}>
                      {header.header}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {endpointsWithIds?.map((row, index) => (
                  <React.Fragment key={`${row.id}_${index}`}>
                    <EndpointRow
                      headers={ENDPOINT_HEADERS}
                      row={row}></EndpointRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );
      }}
    />
  );
};

export default Endpoints;
